import { SupportedLocales } from '@/models/localization.model';
import { ProductFieldSources } from '@/models/products.model';
import { PhotoItem } from '@/modules/Products/helpers/configs';

export enum ProductsFiltersFields {
  TITLE = 'name',
  STORE = 'store',
  PARTNER_IDS = 'store_ids',
  CATEGORY_IDS = 'category_ids',
  SKU = 'sku',
  EXTERNAL_ID = 'external_id',
  ACTIVE = 'active',
  ONLY_ZERO_PRICE = 'only_zero_price',
  ONLY_WITHOUT_UKTZED = 'only_without_uktzed',
  ONLY_WITHOUT_DIMENSIONS = 'only_without_dimensions',
  LIMIT = 'limit',
}

export enum ProductsOfMonthFiltersFields {
  STORE_ID = 'store_ids',
  CATEGORY_ID = 'category_id',
  PRODUCT_SKU = 'product_sku',
  DATE_TO = 'date_to',
  DATE_FROM = 'date_from',
  PUBLISHED = 'published',
  NAME = 'name',
  LIMIT = 'limit',
}

export enum ProductsOfMonthFields {
  PARTNER_ID = 'partner_ids',
  PRODUCT_ID = 'product_id',
  DATE_TO = 'date_to',
  DATE_FROM = 'date_from',
  PUBLISHED = 'published',
}

export enum TopProductsFields {
  PRODUCT_ID = 'product_id',
}

export enum ProductFields {
  START_DATE = 'start_date',
  END_DATE = 'expiration_date',
  ACTIVE = 'active',
  WEB_MEDIA = 'web_media',
  MOBILE_MEDIA = 'mobile_media',
  LOCALE = 'locale',
}

export enum BulkAction {
  DELETE = 'delete',
  HIDE = 'hide',
}

export enum MeasureUnitCode {
  PIECE = 'piece',
  GRAM = 'gram',
  KILOGRAM = 'kilogram',
  TON = 'ton',
  LITER = 'liter',
  METER = 'meter',
  LINEAR_METER = 'linear_meter',
  SQUARE_METER = 'square_meter',
  CUBIC_METER = 'cubic_meter',
  PACKAGE = 'package',
  SET = 'set',
  PAIR = 'pair',
  SERVICE = 'service',
  BAY = 'bay',
  PACK = 'pack',
  REEL = 'reel',
}

export type TGetProductsQuery = {
  [ProductsFiltersFields.TITLE]?: string;
  [ProductsFiltersFields.STORE]?: string;
  [ProductsFiltersFields.CATEGORY_IDS]?: string[];
  [ProductsFiltersFields.PARTNER_IDS]?: string[];
  [ProductsFiltersFields.SKU]?: string;
  [ProductsFiltersFields.EXTERNAL_ID]?: string;
  [ProductsFiltersFields.ACTIVE]?: '0' | '1';
  [ProductsFiltersFields.ONLY_ZERO_PRICE]?: '0' | '1';
  [ProductsFiltersFields.ONLY_WITHOUT_UKTZED]?: '0' | '1';
  [ProductsFiltersFields.ONLY_WITHOUT_DIMENSIONS]?: '0' | '1';
  [ProductsFiltersFields.LIMIT]?: number;
  page?: number;
};

export type TGetProductsOfMonthForm = {
  [ProductsOfMonthFields.PARTNER_ID]?: string[];
  [ProductsOfMonthFields.PRODUCT_ID]?: string;
  [ProductsOfMonthFields.DATE_FROM]?: string;
  [ProductsOfMonthFields.DATE_TO]?: string;
  [ProductsOfMonthFields.PUBLISHED]?: '0' | '1';
};

export type TGetTopProductsForm = {
  [TopProductsFields.PRODUCT_ID]: string;
};

export type TGetProductsOfMonthQuery = {
  [ProductsOfMonthFiltersFields.STORE_ID]?: string;
  [ProductsOfMonthFiltersFields.CATEGORY_ID]?: string;
  [ProductsOfMonthFiltersFields.PRODUCT_SKU]?: string;
  [ProductsOfMonthFiltersFields.DATE_FROM]?: string;
  [ProductsOfMonthFiltersFields.DATE_TO]?: string;
  [ProductsOfMonthFiltersFields.PUBLISHED]?: '0' | '1';
  [ProductsOfMonthFiltersFields.NAME]?: string;
  [ProductsOfMonthFiltersFields.LIMIT]?: number;
  page?: number;
};

export enum ProductMainInfoFormFieldsNames {
  LOCALE = 'locale',
  SLUG = 'slug',
  SKU = 'sku',
  EXTERNAL_ID = 'external_id',
  UKTZED = 'uktzed',
  IMPORTED = 'imported',
  ID = 'id',
  PHOTOS = 'photos',
  WEIGHT = 'weight',
  LENGTH = 'length',
  HEIGHT = 'height',
  WIDTH = 'width',
  IS_CUSTOM_ORDER = 'is_custom_order',
  UNIT_CODE = 'unit_code',
  PACKAGE_QUANTITY = 'package_quantity',
  MIN_ORDER_PACKAGES = 'min_order_packages',
}

export enum ProductCharacteristicsFormFieldsNames {
  CATEGORY = 'category',
}

export enum ProductSeoFormFieldsNames {
  LOCALE = 'locale',
}

export enum RegionsAndPricesFormFieldNames {
  AVAILABLE = 'available',
  PRICE = 'price',
  DISCOUNT_PRICE = 'discount_price',
}

export type TProductLocalizedFields = {
  name: string;
  description: string;
  original_name?: string | null;
};

export type TProductSeoLocalizedFields = {
  seo_title: string;
  seo_description: string;
  seo_keywords: string[];
  seo_text: string;
};

export type TProductMainInfoFormValues = {
  [ProductMainInfoFormFieldsNames.LOCALE]: {
    [key in SupportedLocales]: TProductLocalizedFields;
  };
  [ProductMainInfoFormFieldsNames.SLUG]: string;
  [ProductMainInfoFormFieldsNames.EXTERNAL_ID]: string;
  [ProductMainInfoFormFieldsNames.ID]: string;
  [ProductMainInfoFormFieldsNames.UKTZED]: string;
  [ProductMainInfoFormFieldsNames.WEIGHT]: number;
  [ProductMainInfoFormFieldsNames.WIDTH]: number;
  [ProductMainInfoFormFieldsNames.LENGTH]: number;
  [ProductMainInfoFormFieldsNames.HEIGHT]: number;
  [ProductMainInfoFormFieldsNames.IMPORTED]: '0' | '1';
  [ProductMainInfoFormFieldsNames.PHOTOS]: PhotoItem[];
  [ProductMainInfoFormFieldsNames.IS_CUSTOM_ORDER]: '0' | '1';
  [ProductMainInfoFormFieldsNames.UNIT_CODE]?: MeasureUnitCode;
  [ProductMainInfoFormFieldsNames.PACKAGE_QUANTITY]?: number;
  [ProductMainInfoFormFieldsNames.MIN_ORDER_PACKAGES]?: number;
  field_sources?: ProductFieldSources | null;
};

export type TRegionsAndPricesFormValues = {
  [RegionsAndPricesFormFieldNames.AVAILABLE]: boolean;
  [RegionsAndPricesFormFieldNames.PRICE]: number;
  [RegionsAndPricesFormFieldNames.DISCOUNT_PRICE]: number;
};

export type TProductCharacteristicsFormValues = {
  [ProductCharacteristicsFormFieldsNames.CATEGORY]: string;
  [key: string]: string | string[];
};

export type TProductSeoFormValues = {
  [ProductSeoFormFieldsNames.LOCALE]: {
    [key in SupportedLocales]: TProductSeoLocalizedFields;
  };
};

export enum CreateProductFormFieldsNames {
  LOCALE = 'locale',
  SLUG = 'slug',
  // SKU = 'sku',
  EXTERNAL_ID = 'external_id',
  UKTZED = 'uktzed',
  IMPORTED = 'imported',
  ID = 'id',
  PHOTOS = 'photos',
  WEIGHT = 'weight',
  LENGTH = 'length',
  HEIGHT = 'height',
  WIDTH = 'width',
  IS_CUSTOM_ORDER = 'is_custom_order',
  STORE_ID = 'store_id',
  UNIT_CODE = 'unit_code',
  PACKAGE_QUANTITY = 'package_quantity',
  MIN_ORDER_PACKAGES = 'min_order_packages',
}

export type TCreateProductFormValues = {
  [CreateProductFormFieldsNames.LOCALE]: {
    [key in SupportedLocales]: TProductLocalizedFields;
  };
  [CreateProductFormFieldsNames.SLUG]: string;
  [CreateProductFormFieldsNames.EXTERNAL_ID]: string;
  [CreateProductFormFieldsNames.ID]: string;
  [CreateProductFormFieldsNames.UKTZED]: string;
  [CreateProductFormFieldsNames.WEIGHT]: number;
  [CreateProductFormFieldsNames.WIDTH]: number;
  [CreateProductFormFieldsNames.LENGTH]: number;
  [CreateProductFormFieldsNames.HEIGHT]: number;
  [CreateProductFormFieldsNames.IMPORTED]: '0' | '1';
  [CreateProductFormFieldsNames.PHOTOS]: PhotoItem[];
  [CreateProductFormFieldsNames.IS_CUSTOM_ORDER]: '0' | '1';
  [CreateProductFormFieldsNames.STORE_ID]: string;
  [CreateProductFormFieldsNames.UNIT_CODE]: MeasureUnitCode;
  [CreateProductFormFieldsNames.PACKAGE_QUANTITY]?: number;
  [CreateProductFormFieldsNames.MIN_ORDER_PACKAGES]?: number;
};
